import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, ViewChild } from '@angular/core';
import { DialogService, DialogRef } from './components/dialog';
import { SnackbarService } from './components/snackbar/snackbar.service';
import { SidenavComponent } from './components/sidenav/sidenav/sidenav.component';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  RouterState,
} from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { filter, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ESpecificItem } from './components/sidebar/entities/sidebar.enums';
import { SidebarService } from './components/sidebar/sidebar.service';
import { SimpleDialogComponent } from './components/simple-dialog/simple-dialog.component';
import { AuthService } from './core/auth.service';
import { BaseClass } from './core/base-class';
import { SessionService } from './core/session.service';
import { ProfilePopupComponent } from './profile-popup/profile-popup.component';
import { Analytics, EVENT } from './services/analytics';
import { BrowserDetectorService } from './services/browser-detector.service';
import { ConsentService } from './services/consent.service';
import { ContentsquareService } from './services/contentsquare.service';
import { NotificationService } from './services/notification.service';
import { TitleService } from './services/title.service';
import { UpdateService } from './services/update.service';
import { CustomLogMonitor } from './shared/custom-log-monitor';
import { Company } from './shared/model/company';
import * as Notification from './shared/model/notification';
import * as Sentry from '@sentry/angular';
import { isStorageAvailable } from './shared/utils/safe-storage';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseClass implements OnInit {
  @ViewChild('sidenavMenu', { static: true }) sidenavMenu: SidenavComponent;
  @ViewChild('sidenavNotifications', { static: true })
  sidenavNotifications: SidenavComponent;

  profileDialogRef: DialogRef<ProfilePopupComponent>;
  loading = false;
  updateAvailable = false;
  maintenanceDialogRef: DialogRef<SimpleDialogComponent>;
  company$: Observable<Company>;
  notificationPopup = false;
  showCookiesBanner = false;

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(map((result) => result.matches));

  constructor(
    private analytics: Analytics,
    private breakpointObserver: BreakpointObserver,
    private dialog: DialogService,
    private logger: NGXLogger,
    private router: Router,
    private snackbarService: SnackbarService,
    private titleService: TitleService,
    private updateService: UpdateService,
    private browserDetector: BrowserDetectorService,
    private consentService: ConsentService,
    private contentsquareService: ContentsquareService,
    public authService: AuthService,
    public notificationService: NotificationService,
    public sessionService: SessionService,
    public sidebarService: SidebarService
  ) {
    super();
    window['pdfWorkerSrc'] = '/assets/js/pdf.worker.min.mjs';

    this.logger.registerMonitor(new CustomLogMonitor(analytics));

    this.company$ = sessionService.currentCompany$;
  }

  ngOnInit(): void {
    // Flag storage-blocked sessions so support can correlate "blank page /
    // can't log in" reports. The app now degrades instead of crashing, which
    // would otherwise make this failure mode invisible in telemetry.
    if (!isStorageAvailable()) {
      Sentry.setTag('storage_blocked', 'true');
    }

    this.handleCookiesBanner();

    this.analytics.startTracking();
    this.analytics.event(EVENT.APP_INIT, { version: environment.version });

    this.contentsquareService.init();

    this.updateService.detection();
    this.router.events
      .pipe(
        tap((event) => {
          switch (true) {
            case event instanceof NavigationStart: {
              this.loading = true;
              break;
            }
            case event instanceof NavigationEnd:
            case event instanceof NavigationCancel: {
              setTimeout(() => (this.loading = false));
              break;
            }
            case event instanceof NavigationError: {
              setTimeout(() => (this.loading = false));
              this.handleNavigationError(event as NavigationError);
              break;
            }
            default: {
              break;
            }
          }
        }),
        filter((event) => event instanceof NavigationEnd),
        withLatestFrom(this.isHandset$)
      )
      .subscribe(([t, isHandset]) => {
        const { title, subtitle } = this.getTitle(
          this.router.routerState,
          this.router.routerState.root
        );
        this.titleService.setTitle(title[title.length - 1], subtitle);
        this.analytics.pageView((t as RouterEvent)?.url);
        this.closeNotifications();

        if (isHandset) {
          this.sidenavMenu.close();
        }
      });

    this.sidebarService.toggleMenu$.subscribe(() => this.sidenavMenu.toggle());

    this.sidebarService.changedItem$.subscribe((itemType: ESpecificItem) => {
      switch (itemType) {
        case ESpecificItem.profile:
          this.sidenavNotifications.close();
          break;
        default:
          this.sidenavNotifications.close();
      }
    });

    this.notificationService.notifications$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((notifications: Notification.Interface[]) => {
        this.notificationPopup = !!notifications?.length;
        setTimeout(() => {
          this.notificationPopup = false;
        }, 30000);
      });

    this.notificationService.messagesSubj
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((message) => {
        this.openSnackBar(message, 'Fechar');
      });
  }

  private handleNavigationError(event: NavigationError): void {
    Sentry.captureException(event.error, {
      tags: { flow: 'navigation_error' },
      extra: { url: event.url },
    });
    // Only rescue the INITIAL navigation: with no page yet committed an aborted
    // nav leaves an empty outlet (blank page). On a later nav error the user
    // already has a rendered page, so redirecting to /error would be more
    // disruptive than leaving them put.
    if (!this.router.navigated && !this.router.url.startsWith('/error')) {
      this.router.navigateByUrl('/error');
    }
  }

  getTitle(
    state: RouterState,
    parent: ActivatedRoute
  ): { title: string[]; subtitle?: string } {
    const data = [];
    let subtitle: string | undefined;

    if (parent?.snapshot?.data?.title) {
      data.push(parent.snapshot.data.title);
    }

    if (parent?.snapshot?.data?.subtitle) {
      subtitle = parent.snapshot.data.subtitle;
    }

    if (state && parent) {
      const childData = this.getTitle(state, state['firstChild'](parent));
      data.push(...childData.title);
      if (childData.subtitle) {
        subtitle = childData.subtitle;
      }
    }

    return { title: data, subtitle };
  }

  openSnackBar(message: string, action?: string): void {
    this.snackbarService.status(
      { state: 'error', message, action: action || '', icon: '' },
      5000
    );
  }

  onActivate(): void {
    window.scroll(0, 0);
  }

  closeNotifications(): void {
    this.sidebarService.setSpecificItem();
    this.sidenavNotifications.close();
  }

  toggleProfile(): void {
    if (this.profileDialogRef) {
      this.profileDialogRef.close();
    } else {
      this.profileDialogRef = this.dialog.open(ProfilePopupComponent, {
        position: { bottom: '54px', left: '150px' },
        backdropClass: 'profile-backdrop',
        autoFocus: false,
        panelClass: 'zero-padding-dialog',
      });

      this.profileDialogRef.afterClosed().subscribe(() => {
        this.profileDialogRef = undefined;
        this.sidebarService.setSpecificItem(undefined);
      });
    }
  }

  handleCookiesBanner(): void {
    // First-visit auto-show: skip on Brave (it auto-suppresses third-party
    // cookies, so the banner is redundant there). React to consent state
    // changes so a decision taken in another tab — handled via
    // ConsentService's storage listener — auto-hides the banner here too.
    if (!this.browserDetector.isBrave) {
      this.consentService.consent$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((state) => {
          this.showCookiesBanner = state === null;
        });
    }
    // User-initiated reopen ("Gerenciar cookies" in the profile popup)
    // must work on every browser — it is the LGPD-required path for
    // reviewing/revoking consent. Always wired, including Brave.
    this.consentService.reopen$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.showCookiesBanner = true;
      });
  }

  onCookiesAccepted(): void {
    this.showCookiesBanner = false;
  }

  onCookiesClosed(): void {
    // Reopened banner dismissed without changes — keep the stored choice.
    this.showCookiesBanner = false;
  }
}
